/**
 * Общие функции для работы с API
 */

import axios from 'axios';
import { API_URL, SOURCE_NAMES } from './constants';

/**
 * Создает заголовки с авторизацией
 * @returns {Object} Объект с заголовками
 */
export const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Получает имя источника для отображения
 * @param {string} source - Код источника (yandex, youtube, spotify, soundcloud)
 * @returns {string} Название источника
 */
export const getSourceName = (source) => {
  return SOURCE_NAMES[source] || SOURCE_NAMES.yandex;
};

/**
 * Обрабатывает ошибку API запроса
 * @param {Error} error - Ошибка от axios
 * @param {string} defaultMessage - Сообщение по умолчанию
 * @returns {string} Сообщение об ошибке
 */
export const handleApiError = (error, defaultMessage = 'Произошла ошибка') => {
  if (error.response?.data?.error) {
    return error.response.data.error;
  }
  if (error.message) {
    return error.message;
  }
  return defaultMessage;
};

/**
 * Добавляет API_URL к относительному пути
 * @param {string} url - URL (может быть относительным или абсолютным)
 * @returns {string} Полный URL
 */
export const getFullUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  return `${API_URL}${url.startsWith('/') ? '' : '/'}${url}`;
};

/**
 * Выполняет поиск на всех платформах одновременно
 * @param {string} query - Поисковый запрос
 * @returns {Promise<Array>} Массив результатов с разных платформ
 */
export const withStreamUrl = (track, source) => {
  const src = source || track.source || 'yandex';
  let streamUrl = null;
  if (src === 'yandex') {
    streamUrl = track.id ? `/api/yandex/stream?track_id=${track.id}` : null;
  } else if (src === 'youtube') {
    streamUrl = track.url ? `/api/youtube/stream?url=${encodeURIComponent(track.url)}` : null;
  } else if (src === 'spotify') {
    const ref = track.id || track.url;
    streamUrl = ref ? `/api/spotify/stream?track_id=${encodeURIComponent(ref)}` : null;
  } else if (src === 'soundcloud') {
    streamUrl = track.url ? `/api/soundcloud/stream?url=${encodeURIComponent(track.url)}` : (track.id ? `/api/soundcloud/stream?track_id=${encodeURIComponent(track.id)}` : null);
  }
  return { ...track, source: src, streamUrl };
};

export const searchAllPlatforms = async (query, onProgress) => {
  try {
    const sources = ['yandex', 'youtube', 'spotify', 'soundcloud'];
    const endpoints = {
      yandex: '/api/search',
      youtube: '/api/youtube/search',
      spotify: '/api/spotify/search',
      soundcloud: '/api/soundcloud/search'
    };
    const buckets = {};
    const collect = () => sources.flatMap(s => buckets[s] || []);

    await Promise.all(sources.map(async (s) => {
      try {
        const res = await axios.get(`${API_URL}${endpoints[s]}`, { params: { q: query }, timeout: 45000 });
        buckets[s] = Array.isArray(res.data) ? res.data.map(t => withStreamUrl(t, s)) : [];
      } catch (e) {
        buckets[s] = [];
      }
      // Показываем то, что уже пришло, не дожидаясь самых медленных источников
      if (onProgress) onProgress(collect());
    }));

    return collect();
  } catch (error) {
    console.error('Ошибка поиска на всех платформах:', error);
    return [];
  }
};

/**
 * Определяет тип URL и платформу
 * @param {string} url - URL для проверки
 * @returns {Object} { type: 'youtube'|'yandex'|'spotify'|'soundcloud'|'unknown', isPlaylist: boolean }
 */
export const detectUrlType = (url) => {
  if (!url || typeof url !== 'string') {
    return { type: 'unknown', isPlaylist: false };
  }

  const isYouTubeUrl = /(?:youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/|playlist)|youtu\.be\/)/.test(url);
  const isYandexUrl = /music\.yandex\.(?:ru|com|by|kz|uz)\//.test(url) && /\/(?:track|album|playlists?|users)\//.test(url);
  const isSpotifyUrl = /open\.spotify\.com\/(?:intl-[a-z-]+\/)?(?:track|album|playlist|artist)\//.test(url);
  const cleanUrl = url.split('?')[0].replace(/\/$/, '');
  const isSoundcloudUrl = /^https?:\/\/(?:(?:www|m)\.)?soundcloud\.com\/[^/]+/.test(cleanUrl) || /^https?:\/\/on\.soundcloud\.com\//.test(cleanUrl);

  if (isYouTubeUrl) {
    return { type: 'youtube', isPlaylist: url.includes('playlist') };
  }
  if (isYandexUrl) {
    const isAlbum = /\/album\/\d+/.test(url) && !url.includes('/track/');
    return { type: 'yandex', isPlaylist: url.includes('/playlists/') || isAlbum };
  }
  if (isSpotifyUrl) {
    return { type: 'spotify', isPlaylist: url.includes('/playlist/') || url.includes('/album/') };
  }
  if (isSoundcloudUrl) {
    const segments = cleanUrl.replace(/^https?:\/\/[^/]+\//, '').split('/');
    const isShort = /on\.soundcloud\.com/.test(cleanUrl);
    const isSet = segments[1] === 'sets' || segments[1] === 'tracks';
    const isProfile = segments.length === 1 && !isShort;
    return { type: 'soundcloud', isPlaylist: isSet || isProfile };
  }

  return { type: 'unknown', isPlaylist: false };
};

